// src/components/RequestFlow/Steps/DataEntry/Sections/BankInformation.js
import React, { useState, useImperativeHandle, forwardRef, useEffect, useRef, useCallback } from 'react';
import { faCheckCircle, faExclamationTriangle, faBuildingColumns, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BANKS } from '../../RequestSetupStep';
import { validateCustomerCbu } from '../../../../../services/comersBankValidation';
import { getCustomerRouteKey } from '../../../../../utils/customerRoute';

const REMOTE_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  VALID: 'valid',
  INVALID: 'invalid',
};

// En no-producción Coinag solo responde CBUs de prueba: se muestra el error remoto
// pero no se bloquea el avance del formulario. En production sí es bloqueante.
const isRemoteCbuBlocking = process.env.REACT_APP_ENVIRONMENT === 'production';

const BankInformation = forwardRef((props, ref) => {
  const {
    formData,
    handleChange,
    activeSection,
    sectionName,
    sectionValidity,
    setSectionValidity,
    handleSectionClick,
    bankData, // Datos del banco seleccionado previamente
    suggestedBankInfo,
    clientData,
    displayMode = 'accordion'
  } = props;

  const isContinuousMode = displayMode === 'continuous';

  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [remoteValidation, setRemoteValidation] = useState({
    status: REMOTE_STATUS.IDLE,
    message: '',
  });
  const userModifiedCbuRef = useRef(false);
  const requiredFields = ['cbu'];

  useEffect(() => {
    const allFieldsTouched = requiredFields.every(field => touchedFields[field]);
    if (allFieldsTouched) {
      validateSection();
    }
    // eslint-disable-next-line
  }, [touchedFields]);

  useEffect(() => {
    // Validar automáticamente si hay datos del banco
    if (resolveSelectedBank() && formData.cbu) {
      validateSection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankData, formData?.requestSetup?.bank, formData.cbu]);

  useEffect(() => {
    if (!suggestedBankInfo?.cbu || formData.cbu || userModifiedCbuRef.current) {
      return;
    }

    if (!suggestedBankMatchesSelectedBank(suggestedBankInfo)) {
      return;
    }

    handleChange({ target: { name: 'cbu', value: suggestedBankInfo.cbu } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedBankInfo, formData.cbu, bankData, formData?.requestSetup?.bank]);

  const resolveSelectedBank = () => {
    if (bankData && (bankData.id || bankData.code)) {
      return bankData;
    }

    const selectedBankCode = formData?.requestSetup?.bank;
    if (!selectedBankCode) {
      return null;
    }

    const matchedBank = BANKS.find((bank) => String(bank.code) === String(selectedBankCode));
    if (!matchedBank) {
      return null;
    }

    return {
      id: matchedBank.code,
      code: matchedBank.code,
      name: matchedBank.name
    };
  };

  const validateCBUStructure = (cbu, expectedBankCode = null) => {
    if (cbu.length !== 22) {
      return false;
    }

    const firstBlock = cbu.substring(0, 8);
    const secondBlock = cbu.substring(8, 22);

    const bankCode = firstBlock.substring(0, 3);
    const branchCode = firstBlock.substring(3, 7);
    const firstCheckDigit = Number(firstBlock.substring(7, 8));

    if (expectedBankCode && bankCode !== expectedBankCode) {
      return false;
    }

    const suma1 =
      Number(bankCode[0]) * 7 +
      Number(bankCode[1]) * 1 +
      Number(bankCode[2]) * 3 +
      Number(branchCode[0]) * 9 +
      Number(branchCode[1]) * 7 +
      Number(branchCode[2]) * 1 +
      Number(branchCode[3]) * 3;

    const diferencia1 = 10 - (suma1 % 10);
    const validFirstBlock = firstCheckDigit === (diferencia1 === 10 ? 0 : diferencia1);

    const accountNumber = secondBlock.substring(0, 13);
    const secondCheckDigit = Number(secondBlock.substring(13, 14));

    const suma2 =
      Number(accountNumber[0]) * 3 +
      Number(accountNumber[1]) * 9 +
      Number(accountNumber[2]) * 7 +
      Number(accountNumber[3]) * 1 +
      Number(accountNumber[4]) * 3 +
      Number(accountNumber[5]) * 9 +
      Number(accountNumber[6]) * 7 +
      Number(accountNumber[7]) * 1 +
      Number(accountNumber[8]) * 3 +
      Number(accountNumber[9]) * 9 +
      Number(accountNumber[10]) * 7 +
      Number(accountNumber[11]) * 1 +
      Number(accountNumber[12]) * 3;

    const diferencia2 = 10 - (suma2 % 10);
    const validSecondBlock = secondCheckDigit === (diferencia2 === 10 ? 0 : diferencia2);

    return validFirstBlock && validSecondBlock;
  };

  const normalizeBankCode = (value) => String(value || '').padStart(3, '0');

  const suggestedBankMatchesSelectedBank = (bankInfo) => {
    const selectedBank = resolveSelectedBank();
    if (!selectedBank || !bankInfo?.bank_id) {
      return false;
    }

    const suggestedBankId = String(bankInfo.bank_id);
    const selectedBankId = selectedBank.id !== undefined && selectedBank.id !== null
      ? String(selectedBank.id)
      : '';

    if (selectedBankId && selectedBankId === suggestedBankId) {
      return true;
    }

    if (selectedBankId && Number(selectedBankId) === Number(suggestedBankId)) {
      return true;
    }

    const selectedBankCode = selectedBank.code ? normalizeBankCode(selectedBank.code) : '';
    const suggestedCbuBankCode = bankInfo.cbu ? String(bankInfo.cbu).slice(0, 3) : '';

    return Boolean(selectedBankCode && selectedBankCode === suggestedCbuBankCode);
  };

  const validateCBU = (cbu) => {
    if (!cbu) {
      return 'El CBU es requerido';
    }

    const cleanCBU = cbu.replace(/[\s-]/g, '');

    if (!/^\d{22}$/.test(cleanCBU)) {
      return 'El CBU debe tener exactamente 22 dígitos';
    }

    const selectedBank = resolveSelectedBank();
    const expectedBankCode = selectedBank?.code ? String(selectedBank.code).padStart(3, '0') : null;

    if (!validateCBUStructure(cleanCBU, expectedBankCode)) {
      const cbuBankCode = cleanCBU.substring(0, 3);

      if (expectedBankCode && cbuBankCode !== expectedBankCode) {
        return 'El CBU no corresponde al banco seleccionado';
      }

      return 'El CBU ingresado no es valido';
    }

    return '';
  };

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'cbu':
        error = validateCBU(value);
        break;
      default:
        break;
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));

    return !error;
  };

  /**
   * Consulta banks_middleware via lenders_backoffice para verificar que el CBU
   * pertenezca al titular de la solicitud. Se dispara al salir del campo cuando
   * la validación local ya pasó.
   */
  const validateCbuRemote = useCallback(async (cbu) => {
    const customerKey = getCustomerRouteKey(clientData);

    if (!customerKey) {
      return;
    }

    setRemoteValidation({ status: REMOTE_STATUS.LOADING, message: '' });

    try {
      await validateCustomerCbu(customerKey, cbu);

      setRemoteValidation({ status: REMOTE_STATUS.VALID, message: '' });
      setErrors((prev) => ({ ...prev, cbu: '' }));
      setSectionValidity((prev) => ({ ...prev, [sectionName]: true }));
    } catch (err) {
      const serverMessage =
        err?.response?.data?.errors?.cbu?.[0] ||
        err?.response?.data?.message ||
        'No fue posible verificar el CBU. Intente nuevamente.';

      setRemoteValidation({ status: REMOTE_STATUS.INVALID, message: serverMessage });
      setErrors((prev) => ({ ...prev, cbu: serverMessage }));
      // Fuera de production se muestra el aviso pero se permite continuar
      setSectionValidity((prev) => ({
        ...prev,
        [sectionName]: !isRemoteCbuBlocking,
      }));
    }
  }, [clientData, sectionName, setSectionValidity]);

  const validateSection = () => {
    const selectedBank = resolveSelectedBank();

    // Verificar que tengamos banco seleccionado
    if (!selectedBank || (!selectedBank.id && !selectedBank.code)) {
      setSectionValidity((prev) => ({ ...prev, [sectionName]: false }));
      return false;
    }

    // Validar CBU local
    const cbuValid = validateField('cbu', formData.cbu);

    if (!cbuValid) {
      setSectionValidity((prev) => ({ ...prev, [sectionName]: false }));
      return false;
    }

    // Verificar resultado de la validación remota
    if (remoteValidation.status === REMOTE_STATUS.VALID) {
      setSectionValidity((prev) => ({ ...prev, [sectionName]: true }));
      return true;
    }

    if (remoteValidation.status === REMOTE_STATUS.INVALID) {
      setErrors((prev) => ({ ...prev, cbu: remoteValidation.message }));
      if (!isRemoteCbuBlocking) {
        setSectionValidity((prev) => ({ ...prev, [sectionName]: true }));
        return true;
      }
      setSectionValidity((prev) => ({ ...prev, [sectionName]: false }));
      return false;
    }

    // Estado loading o idle: la consulta remota aún no completó, no se puede avanzar
    setSectionValidity((prev) => ({ ...prev, [sectionName]: false }));
    return false;
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;

    // Para CBU, solo permitir dígitos y resetear validación remota al editar
    if (name === 'cbu') {
      userModifiedCbuRef.current = true;
      const cleanValue = value.replace(/\D/g, '');
      if (cleanValue.length <= 22) {
        handleChange({ target: { name, value: cleanValue } });
        setRemoteValidation({ status: REMOTE_STATUS.IDLE, message: '' });
      }
    } else {
      handleChange(e);
    }
  };

  const handleFieldBlur = (e) => {
    const { name, value } = e.target;
    setTouchedFields(prev => ({ ...prev, [name]: true }));

    const localValid = validateField(name, value);

    // Si el CBU es localmente válido, disparar validación remota
    if (name === 'cbu' && localValid) {
      validateCbuRemote(value);
    }
  };

  const formatCBU = (cbu) => {
    if (!cbu) return '';
    // Formatear CBU en grupos de 4 dígitos para mejor legibilidad
    const cleanCBU = cbu.replace(/\D/g, '');
    return cleanCBU.replace(/(\d{4})(?=\d)/g, '$1-');
  };

  const selectedBank = resolveSelectedBank();
  const isRemoteLoading = remoteValidation.status === REMOTE_STATUS.LOADING;

  const cbuInputClass = () => {
    if (errors.cbu) return 'is-invalid';
    if (remoteValidation.status === REMOTE_STATUS.VALID) return 'is-valid';
    if (touchedFields.cbu && !errors.cbu && remoteValidation.status === REMOTE_STATUS.IDLE) return '';
    return '';
  };

  useImperativeHandle(ref, () => ({
    validateSection,
  }));

  const renderCbuAddon = () => {
    if (isRemoteLoading) {
      return (
        <span className="input-group-text">
          <FontAwesomeIcon icon={faSpinner} spin />
        </span>
      );
    }
    return (
      <span className="input-group-text">
        <FontAwesomeIcon icon={faBuildingColumns} />
      </span>
    );
  };

  // Renderizado condicional basado en el modo de visualización
  if (isContinuousMode) {
    return (
      <div className="continuous-section mb-2">
        <div className="section-header mb-3">
          <h5 className="d-flex align-items-center mb-0 fw-light rg-request-section__title">
            <FontAwesomeIcon icon={faBuildingColumns} className="me-2" />
            Información Bancaria
            {sectionValidity[sectionName] === true && (
              <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2" />
            )}
            {sectionValidity[sectionName] === false && (
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger ms-2" />
            )}
          </h5>
        </div>
        <div className="section-content">
          <div className="row">
            {/* Banco seleccionado (readonly) */}
            <div className="col-md-6 mb-3">
              <label htmlFor="selectedBank" className="form-label">
                Banco seleccionado <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faBuildingColumns} />
                </span>
                <input
                  type="text"
                  className="form-control"
                  id="selectedBank"
                  value={selectedBank ? selectedBank.name : 'No se ha seleccionado un banco'}
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </div>
              {!selectedBank && (
                <div className="form-text text-danger">
                  Debe seleccionar un banco antes de continuar
                </div>
              )}
              {selectedBank && (
                <div className="form-text text-muted">
                  Banco seleccionado al inicio de la solicitud
                </div>
              )}
            </div>

            {/* CBU */}
            <div className="col-md-6 mb-3">
              <label htmlFor="cbu" className="form-label">
                CBU <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                {renderCbuAddon()}
                <input
                  type="text"
                  className={`form-control ${cbuInputClass()}`}
                  id="cbu"
                  name="cbu"
                  value={formData.cbu || ''}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  placeholder="Ingrese su CBU (22 dígitos)"
                  maxLength="22"
                  autoComplete="off"
                  disabled={isRemoteLoading}
                />
              </div>
              {errors.cbu && (
                <div className="invalid-feedback d-block">
                  {errors.cbu}
                </div>
              )}
              {isRemoteLoading && (
                <div className="form-text text-muted">
                  Verificando CBU...
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Modo accordion (original)
  return (
    <div className="accordion-item">
      <h2 className="accordion-header" id={`heading${sectionName}`}>
        <button
          className={`accordion-button ${activeSection !== sectionName ? 'collapsed' : ''}`}
          type="button"
          onClick={() => handleSectionClick(sectionName)}
        >
          <FontAwesomeIcon icon={faBuildingColumns} className="me-2" />
          Información Bancaria{' '}
          {sectionValidity[sectionName] === true && (
            <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2" />
          )}
          {sectionValidity[sectionName] === false && (
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger ms-2" />
          )}
        </button>
      </h2>
      <div
        className={`accordion-collapse collapse ${activeSection === sectionName ? 'show' : ''}`}
        id={`collapse${sectionName}`}
      >
        <div className="accordion-body">
          <div className="row">
            {/* Banco seleccionado (readonly) */}
            <div className="col-md-6 mb-3">
              <label htmlFor="selectedBank" className="form-label">
                Banco seleccionado <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faBuildingColumns} />
                </span>
                <input
                  type="text"
                  className="form-control"
                  id="selectedBank"
                  value={selectedBank ? selectedBank.name : 'No se ha seleccionado un banco'}
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </div>
              {!selectedBank && (
                <div className="form-text text-danger">
                  Debe seleccionar un banco antes de continuar
                </div>
              )}
              {selectedBank && (
                <div className="form-text text-muted">
                  Banco seleccionado al inicio de la solicitud
                </div>
              )}
            </div>

            {/* CBU */}
            <div className="col-md-6 mb-3">
              <label htmlFor="cbu" className="form-label">
                CBU <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                {renderCbuAddon()}
                <input
                  type="text"
                  className={`form-control ${cbuInputClass()}`}
                  id="cbu"
                  name="cbu"
                  value={formData.cbu || ''}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  placeholder="Ingrese su CBU (22 dígitos)"
                  maxLength="22"
                  autoComplete="off"
                  disabled={isRemoteLoading}
                />
              </div>
              {errors.cbu && (
                <div className="invalid-feedback d-block">
                  {errors.cbu}
                </div>
              )}
              {isRemoteLoading && (
                <div className="form-text text-muted">
                  Verificando CBU...
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default BankInformation;
